// Hardcoded fallbacks matching the current homepage exactly. Used when the
// CMS row has NULL (or invalid) values for a field.
import heroImage from "@/assets/hero-home.jpg";
import whyChooseBanner from "@/assets/why-choose-banner.jpg";
import founderPortrait from "@/assets/founder-portrait.jpg";
import trustQualityImage from "@/assets/trust-quality.jpg";
import finalCtaBackground from "@/assets/final-cta-bg.jpg";
import howItWorksImage from "@/assets/meal-lunch.jpg";
import type { HomepagePromotion } from "@/lib/homepage-promotion";

export type HomepageContent = {
  promotion?: HomepagePromotion | null;
  liveAnnouncementEnabled?: boolean;
  heroBadge: string;
  heroTitleLine1: string;
  heroTitleAccent: string;
  heroTitleLine2: string;
  heroSubtitle: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  secondaryButtonText: string;
  secondaryButtonLink: string;
  whyChooseHeading: string;
  whyChooseDescription: string;
  founderHeading: string;
  founderDescription: string;
  mealCategoriesHeading: string;
  mealCategoriesDescription: string;
  ctaHeading: string;
  ctaDescription: string;
  ctaButtonText: string;
  ctaButtonLink: string;
  heroImageUrl: string;
  heroImageAlt: string;
  heroMediaType: "image" | "video";
  heroVideoUrl: string;
  heroVideoMobileUrl: string;
  heroPosterUrl: string;
  whyChooseBannerUrl: string;
  founderImageUrl: string;
  trustQualityImageUrl: string;
  howItWorksEyebrow: string;
  howItWorksHeading: string;
  howItWorksDescription: string;
  howItWorksStep1Title: string;
  howItWorksStep1Description: string;
  howItWorksStep2Title: string;
  howItWorksStep2Description: string;
  howItWorksStep3Title: string;
  howItWorksStep3Description: string;
  howItWorksStep4Title: string;
  howItWorksStep4Description: string;
  howItWorksImageUrl: string;
  howItWorksImageAlt: string;
  finalCtaBackgroundUrl: string;
  // True once the CMS homepage row has been fetched (or failed), so callers
  // can avoid flashing fallback media before the real media type is known.
  isLoaded?: boolean;
};

export const HOMEPAGE_FALLBACKS: HomepageContent = {
  heroBadge: "Mumbai · Est. Kitchen",
  heroTitleLine1: "Nourishing meals,",
  heroTitleAccent: "thoughtfully",
  heroTitleLine2: "delivered.",
  heroSubtitle:
    "Chef-curated, nutrition-first breakfast, lunch, snacks and dinner — made from farm-direct ingredients and delivered fresh across 70+ Mumbai neighbourhoods.",
  primaryButtonText: "View Meal Plans",
  primaryButtonLink: "/meal-plans",
  secondaryButtonText: "Our Story",
  secondaryButtonLink: "/about",
  whyChooseHeading: "Made with care, delivered with intent.",
  whyChooseDescription: "",
  founderHeading: "A kitchen with a conscience.",
  founderDescription:
    "Poonam Kapur's Healthy Kitchen is a socially conscious, nutrition-rich, tech-enabled food company built by a passionate team — food experts curating meals with farm-direct raw materials.",
  mealCategoriesHeading: "Four slots. Every day.",
  mealCategoriesDescription: "",
  ctaHeading: "Ready to eat healthier?",
  ctaDescription:
    "Explore our meal plans and pick the slots that fit your day. We're here if you'd like to chat first.",
  ctaButtonText: "View Meal Plans",
  ctaButtonLink: "/meal-plans",
  heroImageUrl: heroImage,
  heroImageAlt:
    "A thoughtfully plated healthy Indian meal — dal, brown rice, roasted vegetables, roti and fresh salad.",
  // No built-in video: the hero image stays the permanent fallback.
  heroMediaType: "image",
  heroVideoUrl: "",
  heroVideoMobileUrl: "",
  heroPosterUrl: "",
  whyChooseBannerUrl: whyChooseBanner,
  founderImageUrl: founderPortrait,
  trustQualityImageUrl: trustQualityImage,
  howItWorksEyebrow: "How it works",
  howItWorksHeading: "Healthy meals, made simple.",
  howItWorksDescription:
    "Choose your goal, select your meals, and let us take care of the rest — from fresh preparation to doorstep delivery.",
  howItWorksStep1Title: "Choose Your Goal",
  howItWorksStep1Description: "Tell us what you want to achieve with your meals.",
  howItWorksStep2Title: "Choose Your Meals",
  howItWorksStep2Description: "Select a meal plan or choose individual à la carte meals.",
  howItWorksStep3Title: "We Prepare Fresh",
  howItWorksStep3Description:
    "Our kitchen prepares your meals with care, freshness, and nutrition in mind.",
  howItWorksStep4Title: "Get It Delivered",
  howItWorksStep4Description:
    "Fresh meals are delivered to your doorstep across our delivery areas.",
  howItWorksImageUrl: howItWorksImage,
  howItWorksImageAlt: "A freshly prepared Healthy Kitchen meal ready for delivery.",
  finalCtaBackgroundUrl: finalCtaBackground,
};